/**
 * Datos de api-core: catálogo (proxy), lealtad, suscripciones, partners,
 * perfil. Devuelve los shapes crudos del server; la normalización a los
 * tipos de UI vive en los hooks (para minimizar el diff).
 */

import { API_CORE_URL } from './config';
import { apiRequest, apiRequestWithMeta } from './http';

// ---- Eventos (proxy core → taquilla) --------------------------------------

export interface CoreEventDto {
  id: string;
  title: string;
  slug?: string | null;
  short_description?: string | null;
  description?: string | null;
  date?: string | null;
  end_date?: string | null;
  venue?: string | null;
  price?: number | null;
  cover_image?: string | null;
  status?: string | null;
  active?: boolean;
  featured?: boolean;
  total_capacity?: number | null;
  brand_id?: string | null;
  // SPEC-010 — ¿el evento vende boletería? `false` = solo-consumo. La tienda MB
  // (boletería) NO lista los solo-consumo (ver `fetchEvents` en hooks/useEvents).
  uses_ticketing?: boolean | null;
  [key: string]: unknown;
}

/** Lista de eventos publicados (filtrados por el proxy de core). */
export async function listEvents(limit = 100): Promise<CoreEventDto[]> {
  const data = await apiRequest<CoreEventDto[]>(API_CORE_URL, '/events', {
    query: { limit },
  });
  return data ?? [];
}

/** Detalle de un evento por id. */
export async function getEvent(eventId: string): Promise<CoreEventDto> {
  return apiRequest<CoreEventDto>(API_CORE_URL, `/events/${encodeURIComponent(eventId)}`);
}

/**
 * Composición §6 (`GET /v1/events/:id/full`): evento + datos del partner de
 * First Class (flash sales, offers, merchandising, restaurante) + flag de
 * puntos. Lectura pública/cliente. Shape crudo (lo normaliza `useEventFull`).
 */
export type EventFullRaw = Record<string, unknown>;

export async function getEventFull(eventId: string): Promise<EventFullRaw | null> {
  return apiRequest<EventFullRaw | null>(
    API_CORE_URL,
    `/events/${encodeURIComponent(eventId)}/full`,
  );
}

// ---- Flash sales ----------------------------------------------------------

export interface FlashSaleDto {
  id: string;
  title: string;
  description?: string | null;
  type?: string | null;
  original_price?: number | string | null;
  discount_percentage?: number | string | null;
  final_price?: number | string | null;
  quantity_available?: number | null;
  quantity_sold?: number | null;
  max_per_user?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  membership_required?: string | null;
  image_url?: string | null;
  partner_id?: string | null;
  [key: string]: unknown;
}

export async function listFlashSales(): Promise<FlashSaleDto[]> {
  const data = await apiRequest<FlashSaleDto[]>(API_CORE_URL, '/flash-sales', {
    query: { active_only: true, limit: 100 },
  });
  return data ?? [];
}

// ---- Experiencias VIP -----------------------------------------------------

export interface ExperienceDto {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  /** Precio en centavos (minor units) — la UI lo normaliza a pesos enteros. */
  price_cents?: number | string | null;
  category?: string | null;
  location?: string | null;
  duration?: string | null;
  max_capacity?: number | null;
  image_url?: string | null;
  highlights?: string[] | null;
  status?: string | null;
  [key: string]: unknown;
}

/** Lista de experiencias VIP activas (`GET /v1/experiences`). */
export async function listExperiences(limit = 100): Promise<ExperienceDto[]> {
  const data = await apiRequest<ExperienceDto[]>(API_CORE_URL, '/experiences', {
    query: { limit },
  });
  return data ?? [];
}

// ---- Suscripciones / membresías -------------------------------------------

export interface SubscriptionPlanDto {
  id: string;
  name?: string | null;
  display_name?: string | null;
  slug?: string | null;
  description?: string | null;
  price?: number | string | null;
  welcome_points?: number | null;
  monthly_bonus_points?: number | null;
  active?: boolean;
  available_for_purchase?: boolean;
  display_order?: number | null;
  [key: string]: unknown;
}

export interface SubscriptionDto {
  id: string;
  user_id: string;
  plan_id: string;
  cycle: 'monthly' | 'annual' | string;
  started_at?: string | null;
  ends_at?: string | null;
  status: string;
  canceled_reason?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

export async function listSubscriptionPlans(): Promise<SubscriptionPlanDto[]> {
  const data = await apiRequest<SubscriptionPlanDto[]>(API_CORE_URL, '/subscriptions/plans');
  return data ?? [];
}

export async function listMySubscriptions(): Promise<SubscriptionDto[]> {
  const env = await apiRequestWithMeta<SubscriptionDto[]>(API_CORE_URL, '/subscriptions/history', {
    query: { limit: 100 },
  });
  return env.data ?? [];
}

export interface ActivateSubscriptionResult {
  [key: string]: unknown;
}

/**
 * Activa una suscripción (modo cash). El endpoint actual NO soporta pago con
 * puntos / mix; ese gap está registrado en endpoint_gaps.md.
 */
export async function activateSubscription(
  planId: string,
  cycle: 'monthly' | 'annual',
): Promise<ActivateSubscriptionResult> {
  return apiRequest<ActivateSubscriptionResult>(API_CORE_URL, '/subscriptions/activate', {
    method: 'POST',
    body: { plan_id: planId, cycle },
  });
}

// ---- Partners (tienda) ----------------------------------------------------

export interface PartnerItemDto {
  id: string;
  partner_id?: string | null;
  category_id?: string | null;
  name: string;
  description?: string | null;
  price_public?: number | string | null;
  price_firstclass?: number | string | null;
  type?: string | null;
  is_active?: boolean;
  is_featured?: boolean;
  stock?: number | null;
  [key: string]: unknown;
}

export async function listPartnerItems(): Promise<PartnerItemDto[]> {
  // El catálogo ya supera los 200 ítems; pedimos un tope amplio para no
  // truncar productos en la Tienda (la API respeta `total` en `meta`).
  const data = await apiRequest<PartnerItemDto[]>(API_CORE_URL, '/partners/catalog-items', {
    query: { limit: 500, type: 'product' },
  });
  return data ?? [];
}

export interface PartnerDto {
  id: string;
  name: string;
  slug?: string | null;
  type?: string | null;
  logo_url?: string | null;
  cover_image?: string | null;
  main_image?: string | null;
  [key: string]: unknown;
}

/**
 * Lista de partners (storefront). Provee el `name` y `type` reales para
 * resolver el `partner_id` de cada producto del catálogo a una etiqueta
 * legible (la API de catalog-items solo trae `partner_id`/`category_id` como
 * uuid, sin nombre ni slug de categoría).
 */
export async function listPartners(): Promise<PartnerDto[]> {
  const data = await apiRequest<PartnerDto[]>(API_CORE_URL, '/partners', {
    query: { limit: 200 },
  });
  return data ?? [];
}

// ---- Lealtad --------------------------------------------------------------

export interface PointsBalanceDto {
  user_id: string;
  balance: number;
}

export interface TierMeDto {
  user_id: string;
  total_points?: number | null;
  current_tier_name?: string | null;
  current_tier_slug?: string | null;
  [key: string]: unknown;
}

export async function getPointsBalance(): Promise<PointsBalanceDto | null> {
  return apiRequest<PointsBalanceDto | null>(API_CORE_URL, '/loyalty/points/balance');
}

export async function getMyTier(): Promise<TierMeDto | null> {
  return apiRequest<TierMeDto | null>(API_CORE_URL, '/loyalty/tiers/me');
}

// ---- Perfil enriquecido ---------------------------------------------------

export interface ProfileSubscriptionDto {
  active?: boolean;
  plan?: string | null;
  plan_id?: string | null;
  cycle?: 'monthly' | 'annual' | string | null;
  expires_at?: string | null;
}

export interface ProfileMeDto {
  id: string;
  email?: string | null;
  name?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  username?: string | null;
  username_editable?: boolean;
  document_type?: string | null;
  document_number?: string | null;
  referral_code?: string | null;
  member_since?: string | null;
  tier?: string | null;
  points?: number | null;
  lifetime_points?: number | null;
  subscription?: ProfileSubscriptionDto | null;
  [key: string]: unknown;
}

/**
 * Perfil enriquecido del usuario autenticado (`GET /v1/profile/me`): identidad
 * (nombre, teléfono, avatar, username, documento), referral, antigüedad, tier,
 * puntos y suscripción. Es la fuente primaria de la cuenta; las llamadas de
 * lealtad/suscripción quedan como respaldo si éste falla.
 */
export async function getMyProfile(): Promise<ProfileMeDto | null> {
  return apiRequest<ProfileMeDto | null>(API_CORE_URL, '/profile/me');
}

export interface UsernameCanEditDto {
  can_edit: boolean;
}

/** ¿El usuario puede cambiar su username? (`GET /v1/profile/username/can-edit`). */
export async function getUsernameCanEdit(): Promise<boolean> {
  const data = await apiRequest<UsernameCanEditDto | null>(
    API_CORE_URL,
    '/profile/username/can-edit',
  );
  return Boolean(data?.can_edit);
}

/**
 * Cambia el username (`PATCH /v1/profile/username`). Operación de una sola vez
 * validada server-side; devuelve el perfil actualizado.
 */
export async function updateUsername(username: string): Promise<ProfileMeDto | null> {
  return apiRequest<ProfileMeDto | null>(API_CORE_URL, '/profile/username', {
    method: 'PATCH',
    body: { username },
  });
}

export async function getMySubscription(): Promise<SubscriptionDto | null> {
  return apiRequest<SubscriptionDto | null>(API_CORE_URL, '/subscriptions/me');
}

// ---- Fila Virtual de Flash Sales (C3) -------------------------------------

/**
 * Estado del fan en la fila, tal como lo devuelve api-core
 * (`/v1/flash-sales/{id}/queue/*`). El backend habla snake_case; el hook lo
 * normaliza a la forma que ya consumía la Sala de Espera.
 */
export interface FlashQueueStatusDto {
  ticket_id: string;
  flash_sale_id: string;
  /** `waiting` = en la fila · `ready` = turno vigente, puede comprar. */
  status: 'waiting' | 'ready';
  users_ahead: number;
  position: number;
  estimated_seconds: number;
  admitted_until: string | null;
  ticket_number: number;
}

/** Toma un lugar en la fila. Idempotente: reentrar devuelve el mismo lugar. */
export async function joinFlashQueue(flashSaleId: string): Promise<FlashQueueStatusDto> {
  return apiRequest<FlashQueueStatusDto>(
    API_CORE_URL,
    `/flash-sales/${encodeURIComponent(flashSaleId)}/queue/join`,
    { method: 'POST' },
  );
}

/** Mi lugar en la fila. Es lo que se pollea cada 5s desde la Sala de Espera. */
export async function getFlashQueueStatus(flashSaleId: string): Promise<FlashQueueStatusDto> {
  return apiRequest<FlashQueueStatusDto>(
    API_CORE_URL,
    `/flash-sales/${encodeURIComponent(flashSaleId)}/queue/status`,
  );
}

/** Salir de la fila y liberar el lugar para el que viene atrás. */
export async function leaveFlashQueue(flashSaleId: string): Promise<void> {
  await apiRequest(API_CORE_URL, `/flash-sales/${encodeURIComponent(flashSaleId)}/queue`, {
    method: 'DELETE',
  });
}
